import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { MonoTypeOperatorFunction, Observable, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ToastService } from '../../../shared/services/toast.service';
import {
  PageResponse,
  Ticket,
  TicketAgentPerformanceRow,
  TicketAssignRequest,
  TicketAuditLogResponse,
  TicketBacklogTrendRow,
  TicketBulkReassignFromUserRequest,
  TicketBulkReassignResult,
  TicketBulkRequest,
  TicketBulkResult,
  TicketCategoryRootCauseRow,
  TicketCommentRequest,
  TicketCommentResponse,
  TicketCommentUpdateRequest,
  TicketCsatDashboardResponse,
  TicketCsatRequest,
  TicketCsatResponseDto,
  TicketCustomValueResponse,
  TicketCustomValueUpsertRequest,
  TicketEscalateRequest,
  TicketEscalationEventResponse,
  TicketFilterRequest,
  TicketFollowerRequest,
  TicketFollowerResponse,
  TicketKanbanColumnResponse,
  TicketLinkResponse,
  TicketMainDashboardResponse,
  TicketMergedSummary,
  TicketMergeRequest,
  TicketRequest,
  TicketReopenRequest,
  TicketResolutionResponse,
  TicketResolveRequest,
  TicketSearchParams,
  TicketSlaGroupBy,
  TicketSlaPerformanceRow,
  TicketSlaTrackingResponse,
  TicketSplitRequest,
  TicketSplitResponse,
  TicketStageSyncWarningFields,
  TicketSubTicketRequest,
  TicketSubTicketSummary,
  TicketUpdateRequest,
  TicketVolumeRow
} from '../models/tickets.model';

/**
 * Servicio HTTP del módulo Tickets.
 *
 * Multi-tenancy: el backend extrae businessId del JWT — NO se manda en query.
 * Paginación: PageResponse<T> con shape estándar { content, totalElements, totalPages, ... }.
 * Fechas: OffsetDateTime ISO-8601 en UTC; el front convierte a TZ local al pintar.
 */
@Injectable({ providedIn: 'root' })
export class TicketsApiService {
  private readonly apiUrl = `${environment.apiUrl}api`;
  private readonly toast = inject(ToastService);

  constructor(private http: HttpClient) {}

  /**
   * INC 63 — Aviso de sincronización de etapa.
   *
   * Las escrituras que cambian el estado de un ticket (PATCH /tickets/{id},
   * POST /{id}/resolve, POST /{id}/reopen) pueden devolver `stageSyncWarning`:
   * el estado SÍ se guardó, pero la tarjeta del kanban no se movió de columna
   * (pipeline sin etapa de cierre/apertura mapeada, o fallo inesperado).
   *
   * Se emite aquí, en el servicio, y no en cada componente, por dos razones:
   *  1. El aviso es transversal a los 3 endpoints y a todos sus llamadores
   *     (detalle, form, modales de acción) — así ninguno puede olvidarlo.
   *  2. No es feedback de CRUD del componente sino una señal del servidor sobre
   *     un efecto secundario que no ocurrió.
   *
   * Es SIEMPRE `warning` (nunca `error`): la operación se aplicó y la UI no debe
   * revertirse. Duración larga porque el texto es accionable (pide ir a
   * Configuración > Pipelines) y el usuario debe alcanzar a leerlo.
   */
  private warnStageSync<T extends TicketStageSyncWarningFields>(): MonoTypeOperatorFunction<T> {
    return tap((res) => {
      if (res?.stageSyncWarningCode != null && res.stageSyncWarning) {
        this.toast.warning({
          title: 'La tarjeta del kanban no se movió',
          message: res.stageSyncWarning,
          duration: 8000
        });
      }
    });
  }

  // ==========================================================================
  // Listado / búsqueda
  // ==========================================================================

  /** GET /api/tickets — listado paginado simple. */
  searchTickets(params: TicketSearchParams = {}): Observable<PageResponse<Ticket>> {
    let httpParams = new HttpParams();
    if (params.page !== undefined) {
      httpParams = httpParams.set('page', params.page.toString());
    }
    if (params.size !== undefined) {
      httpParams = httpParams.set('size', params.size.toString());
    }
    return this.http.get<PageResponse<Ticket>>(`${this.apiUrl}/tickets`, { params: httpParams });
  }

  /** GET /api/tickets/search?q=... — quick search por número o asunto (hasta 100, sin paginación). */
  quickSearchTickets(q: string): Observable<Ticket[]> {
    const httpParams = new HttpParams().set('q', q);
    return this.http.get<Ticket[]>(`${this.apiUrl}/tickets/search`, { params: httpParams });
  }

  /** POST /api/tickets/search/advanced — búsqueda avanzada AND/OR (RF-13.04). */
  searchTicketsAdvanced(filter: TicketFilterRequest): Observable<PageResponse<Ticket>> {
    return this.http.post<PageResponse<Ticket>>(`${this.apiUrl}/tickets/search/advanced`, filter);
  }

  /** GET /api/tickets/contact/{contactId}/history — historial del contacto. */
  getContactHistory(contactId: number): Observable<Ticket[]> {
    return this.http.get<Ticket[]>(`${this.apiUrl}/tickets/contact/${contactId}/history`);
  }

  // ==========================================================================
  // CRUD individual
  // ==========================================================================

  getTicketById(id: number): Observable<Ticket> {
    return this.http.get<Ticket>(`${this.apiUrl}/tickets/${id}`);
  }

  /**
   * POST /api/tickets. Si `allowDuplicate` es false (default) y existe un ticket
   * abierto del mismo contacto+asunto, el backend responde 409 con
   * { message, duplicates: Ticket[] }. Pasar allowDuplicate=true para forzar.
   */
  createTicket(req: TicketRequest, allowDuplicate = false): Observable<Ticket> {
    let httpParams = new HttpParams();
    if (allowDuplicate) {
      httpParams = httpParams.set('allowDuplicate', 'true');
    }
    return this.http.post<Ticket>(`${this.apiUrl}/tickets`, req, { params: httpParams });
  }

  /** GET /api/tickets/check-duplicate — tickets abiertos coincidentes (vacío si no hay). */
  checkDuplicate(contactId: number, subject: string): Observable<Ticket[]> {
    const httpParams = new HttpParams()
      .set('contactId', contactId.toString())
      .set('subject', subject);
    return this.http.get<Ticket[]>(`${this.apiUrl}/tickets/check-duplicate`, { params: httpParams });
  }

  /**
   * PATCH parcial — sólo los campos modificados. Cambiar idStage puede sincronizar idTicketStatus.
   * Cambiar idTicketStatus puede devolver `stageSyncWarning` (INC 63) → toast de advertencia.
   */
  updateTicket(id: number, patch: TicketUpdateRequest): Observable<Ticket> {
    return this.http
      .patch<Ticket>(`${this.apiUrl}/tickets/${id}`, patch)
      .pipe(this.warnStageSync());
  }

  /**
   * POST /api/tickets/{id}/archive — archiva (V180). No cambia el estado del ticket:
   * lo saca de la lista/kanban y lo deja en la vista de archivados.
   * 409 si tiene sub-tickets/divisiones abiertos.
   */
  archiveTicket(id: number): Observable<Ticket> {
    return this.http.post<Ticket>(`${this.apiUrl}/tickets/${id}/archive`, {});
  }

  /** POST /api/tickets/{id}/unarchive — devuelve el ticket a la operación (V180). */
  unarchiveTicket(id: number): Observable<Ticket> {
    return this.http.post<Ticket>(`${this.apiUrl}/tickets/${id}/unarchive`, {});
  }

  /** DELETE /api/tickets/{id} — 204 ok; 409 si tiene sub-tickets/divisiones abiertos. */
  deleteTicket(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/tickets/${id}`);
  }

  // ==========================================================================
  // Acciones específicas
  // ==========================================================================

  assignTicket(id: number, req: TicketAssignRequest): Observable<Ticket> {
    return this.http.post<Ticket>(`${this.apiUrl}/tickets/${id}/assign`, req);
  }

  /** Reabre el ticket. Puede devolver `stageSyncWarning` (INC 63) → toast de advertencia. */
  reopenTicket(id: number, req: TicketReopenRequest = {}): Observable<Ticket> {
    return this.http
      .post<Ticket>(`${this.apiUrl}/tickets/${id}/reopen`, req)
      .pipe(this.warnStageSync());
  }

  escalateTicket(id: number, req: TicketEscalateRequest): Observable<TicketEscalationEventResponse> {
    return this.http.post<TicketEscalationEventResponse>(`${this.apiUrl}/tickets/${id}/escalate`, req);
  }

  getEscalations(id: number): Observable<TicketEscalationEventResponse[]> {
    return this.http.get<TicketEscalationEventResponse[]>(`${this.apiUrl}/tickets/${id}/escalations`);
  }

  /** Resuelve el ticket. Puede devolver `stageSyncWarning` (INC 63) → toast de advertencia. */
  resolveTicket(id: number, req: TicketResolveRequest): Observable<TicketResolutionResponse> {
    return this.http
      .post<TicketResolutionResponse>(`${this.apiUrl}/tickets/${id}/resolve`, req)
      .pipe(this.warnStageSync());
  }

  getResolution(id: number): Observable<TicketResolutionResponse> {
    return this.http.get<TicketResolutionResponse>(`${this.apiUrl}/tickets/${id}/resolution`);
  }

  // ==========================================================================
  // SLA — runtime tracking (RF-04.04)
  // ==========================================================================

  getSlaTracking(id: number): Observable<TicketSlaTrackingResponse> {
    return this.http.get<TicketSlaTrackingResponse>(`${this.apiUrl}/tickets/${id}/sla`);
  }

  // ==========================================================================
  // CSAT
  // ==========================================================================

  submitCsat(id: number, req: TicketCsatRequest): Observable<TicketCsatResponseDto> {
    return this.http.post<TicketCsatResponseDto>(`${this.apiUrl}/tickets/${id}/csat`, req);
  }

  getCsat(id: number): Observable<TicketCsatResponseDto> {
    return this.http.get<TicketCsatResponseDto>(`${this.apiUrl}/tickets/${id}/csat`);
  }

  // ==========================================================================
  // Audit log (RF-02.06)
  // ==========================================================================

  /**
   * Timeline / auditoría del ticket. El backend expone un Flux (array JSON
   * plano, sin paginar) — NO una página de Spring. La paginación se hace en
   * cliente en el componente de detalle.
   */
  getAuditLog(id: number): Observable<TicketAuditLogResponse[]> {
    return this.http.get<TicketAuditLogResponse[]>(`${this.apiUrl}/tickets/${id}/audit`);
  }

  // ==========================================================================
  // Conversación (comments + custom values)
  // ==========================================================================

  getComments(ticketId: number): Observable<TicketCommentResponse[]> {
    return this.http.get<TicketCommentResponse[]>(`${this.apiUrl}/tickets/${ticketId}/comments`);
  }

  createComment(ticketId: number, req: TicketCommentRequest): Observable<TicketCommentResponse> {
    return this.http.post<TicketCommentResponse>(`${this.apiUrl}/tickets/${ticketId}/comments`, req);
  }

  /** PATCH /api/tickets/{ticketId}/comments/{commentId} — sólo el autor. */
  updateComment(
    ticketId: number,
    commentId: number,
    req: TicketCommentUpdateRequest
  ): Observable<TicketCommentResponse> {
    return this.http.patch<TicketCommentResponse>(
      `${this.apiUrl}/tickets/${ticketId}/comments/${commentId}`,
      req
    );
  }

  /** DELETE /api/tickets/{ticketId}/comments/{commentId} — sólo el autor. */
  deleteComment(ticketId: number, commentId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/tickets/${ticketId}/comments/${commentId}`);
  }

  getCustomValues(ticketId: number): Observable<TicketCustomValueResponse[]> {
    return this.http.get<TicketCustomValueResponse[]>(`${this.apiUrl}/tickets/${ticketId}/custom-values`);
  }

  upsertCustomValue(
    ticketId: number,
    req: TicketCustomValueUpsertRequest
  ): Observable<TicketCustomValueResponse> {
    return this.http.post<TicketCustomValueResponse>(
      `${this.apiUrl}/tickets/${ticketId}/custom-values`,
      req
    );
  }

  // ==========================================================================
  // Sub-tickets / merge / split / links (RF-08.x)
  // ==========================================================================

  createSubTicket(parentId: number, req: TicketSubTicketRequest): Observable<Ticket> {
    return this.http.post<Ticket>(`${this.apiUrl}/tickets/${parentId}/sub-tickets`, req);
  }

  /** GET /api/tickets/{id}/sub-tickets (Inc 53) — subtickets del padre (array, vacío si no hay). */
  getSubTickets(parentId: number): Observable<TicketSubTicketSummary[]> {
    return this.http.get<TicketSubTicketSummary[]>(
      `${this.apiUrl}/tickets/${parentId}/sub-tickets`
    );
  }

  /** GET /api/tickets/{id}/merged (Inc 52) — tickets fusionados en este (array, vacío si no hay). */
  getMergedTickets(id: number): Observable<TicketMergedSummary[]> {
    return this.http.get<TicketMergedSummary[]>(`${this.apiUrl}/tickets/${id}/merged`);
  }

  mergeTickets(req: TicketMergeRequest): Observable<Ticket> {
    return this.http.post<Ticket>(`${this.apiUrl}/tickets/merge`, req);
  }

  splitTicket(id: number, req: TicketSplitRequest): Observable<TicketSplitResponse> {
    return this.http.post<TicketSplitResponse>(`${this.apiUrl}/tickets/${id}/split`, req);
  }

  getLinks(id: number): Observable<TicketLinkResponse[]> {
    return this.http.get<TicketLinkResponse[]>(`${this.apiUrl}/tickets/${id}/links`);
  }

  // ==========================================================================
  // Followers
  // ==========================================================================

  getFollowers(id: number): Observable<TicketFollowerResponse[]> {
    return this.http.get<TicketFollowerResponse[]>(`${this.apiUrl}/tickets/${id}/followers`);
  }

  addFollower(id: number, req: TicketFollowerRequest): Observable<TicketFollowerResponse> {
    return this.http.post<TicketFollowerResponse>(`${this.apiUrl}/tickets/${id}/followers`, req);
  }

  removeFollower(id: number, userId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/tickets/${id}/followers/${userId}`);
  }

  // ==========================================================================
  // Bulk + Admin
  // ==========================================================================

  bulkAction(req: TicketBulkRequest): Observable<TicketBulkResult> {
    return this.http.post<TicketBulkResult>(`${this.apiUrl}/tickets/bulk`, req);
  }

  reassignFromUser(req: TicketBulkReassignFromUserRequest): Observable<TicketBulkReassignResult> {
    return this.http.post<TicketBulkReassignResult>(
      `${this.apiUrl}/tickets/admin/reassign-from-user`,
      req
    );
  }

  // ==========================================================================
  // Kanban (RF-03.04)
  // ==========================================================================

  getKanban(pipelineId?: number): Observable<TicketKanbanColumnResponse[]> {
    let httpParams = new HttpParams();
    if (pipelineId !== undefined) {
      httpParams = httpParams.set('pipelineId', pipelineId.toString());
    }
    return this.http.get<TicketKanbanColumnResponse[]>(`${this.apiUrl}/tickets/kanban`, {
      params: httpParams
    });
  }

  // ==========================================================================
  // Reportes (RF-16.x)
  // ==========================================================================

  /** GET /api/tickets/reports/dashboard — métricas en vivo. */
  getReportsDashboard(): Observable<TicketMainDashboardResponse> {
    return this.http.get<TicketMainDashboardResponse>(`${this.apiUrl}/tickets/reports/dashboard`);
  }

  /** GET /api/tickets/reports/volume — volumen por día. Default últimos 30 días. */
  getVolumeReport(params: { from?: string; to?: string } = {}): Observable<TicketVolumeRow[]> {
    let httpParams = new HttpParams();
    if (params.from) httpParams = httpParams.set('from', params.from);
    if (params.to) httpParams = httpParams.set('to', params.to);
    return this.http.get<TicketVolumeRow[]>(`${this.apiUrl}/tickets/reports/volume`, {
      params: httpParams
    });
  }

  /** GET /api/tickets/reports/sla-performance — agrupado por agent/team/priority/category/pipeline. */
  getSlaPerformanceReport(params: {
    from?: string;
    to?: string;
    groupBy?: TicketSlaGroupBy;
  } = {}): Observable<TicketSlaPerformanceRow[]> {
    let httpParams = new HttpParams();
    if (params.from) httpParams = httpParams.set('from', params.from);
    if (params.to) httpParams = httpParams.set('to', params.to);
    if (params.groupBy) httpParams = httpParams.set('groupBy', params.groupBy);
    return this.http.get<TicketSlaPerformanceRow[]>(
      `${this.apiUrl}/tickets/reports/sla-performance`,
      { params: httpParams }
    );
  }

  /** GET /api/tickets/reports/agent-performance — métricas por agente. */
  getAgentPerformanceReport(params: { from?: string; to?: string } = {}): Observable<
    TicketAgentPerformanceRow[]
  > {
    let httpParams = new HttpParams();
    if (params.from) httpParams = httpParams.set('from', params.from);
    if (params.to) httpParams = httpParams.set('to', params.to);
    return this.http.get<TicketAgentPerformanceRow[]>(
      `${this.apiUrl}/tickets/reports/agent-performance`,
      { params: httpParams }
    );
  }

  /** GET /api/tickets/reports/backlog-trend?days= — evolución de backlog. */
  getBacklogTrendReport(days: number = 30): Observable<TicketBacklogTrendRow[]> {
    const httpParams = new HttpParams().set('days', days.toString());
    return this.http.get<TicketBacklogTrendRow[]>(
      `${this.apiUrl}/tickets/reports/backlog-trend`,
      { params: httpParams }
    );
  }

  /** GET /api/tickets/reports/category-root-cause — top causas raíz por categoría. */
  getCategoryRootCauseReport(params: { from?: string; to?: string } = {}): Observable<
    TicketCategoryRootCauseRow[]
  > {
    let httpParams = new HttpParams();
    if (params.from) httpParams = httpParams.set('from', params.from);
    if (params.to) httpParams = httpParams.set('to', params.to);
    return this.http.get<TicketCategoryRootCauseRow[]>(
      `${this.apiUrl}/tickets/reports/category-root-cause`,
      { params: httpParams }
    );
  }

  /** GET /api/ticket-csat/dashboard — métricas agregadas de CSAT. */
  getCsatDashboard(params: { from?: string; to?: string } = {}): Observable<
    TicketCsatDashboardResponse
  > {
    let httpParams = new HttpParams();
    if (params.from) httpParams = httpParams.set('from', params.from);
    if (params.to) httpParams = httpParams.set('to', params.to);
    return this.http.get<TicketCsatDashboardResponse>(`${this.apiUrl}/ticket-csat/dashboard`, {
      params: httpParams
    });
  }
}
